<script lang="ts" setup>
import { useAnalytics } from "@/composables/useAnalytics";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";

const { t, tm } = useI18n();
const route = useRoute();
const { openModal } = useCalCom();
const { trackEvent, trackFormStep } = useAnalytics();

const nameInput = ref("");
const mailInput = ref("");
const subjectInput = ref("");
const messageInput = ref("");
const form = ref();
const loading = ref(false);

const subjects = computed(() => {
  const keys = ["spendzen", "demo", "other"];
  return keys.map((key) => ({
    key,
    label: t(`spendzen.contact.subjects.${key}`),
  }));
});

const updateSubjectFromQuery = () => {
  let subjectParam = route.query.subject;

  if (Array.isArray(subjectParam)) {
    subjectParam = subjectParam[0];
  }

  if (typeof subjectParam === "string") {
    const cleanedParam = subjectParam.split("?")[0];
    const validKeys = ["spendzen", "demo", "other"];

    if (validKeys.includes(cleanedParam)) {
      subjectInput.value = cleanedParam;
    }
  }
};

watch(
  () => route.query.subject,
  () => {
    updateSubjectFromQuery();
  },
);

// Advanced Form Tracking
const trackedFields = new Set<string>();
const trackFieldInteraction = (fieldName: string, stepNumber: number) => {
  if (!trackedFields.has(fieldName)) {
    trackFormStep("contact_form", stepNumber, fieldName);
    trackedFields.add(fieldName);
  }
};

watch(nameInput, (val) => {
  if (val.length > 0) trackFieldInteraction("name", 1);
});
watch(mailInput, (val) => {
  if (val.length > 0) trackFieldInteraction("email", 2);
});
watch(subjectInput, (val) => {
  if (val) trackFieldInteraction("subject", 3);
});
watch(messageInput, (val) => {
  if (val.length > 0) trackFieldInteraction("message", 4);
});

const props = withDefaults(
  defineProps<{
    title?: string;
    titleHighlight?: string;
    description?: string;
    highlightClass?: string;
  }>(),
  {
    title: "",
    description: "",
    highlightClass: "gradient-text",
  },
);

const { $stableform } = useNuxtApp();

onMounted(async () => {
  updateSubjectFromQuery();

  const stableForm = new $stableform({
    formId: "679f7497c2c271257758179a",
    apiKey: "A3ECDF41-0432-4DEE-9257-F98E3ADDED64",
  })
    .on("success", () => {
      console.log("[StableForm] success");
      nameInput.value = "";
      mailInput.value = "";
      subjectInput.value = "";
      messageInput.value = "";
      loading.value = false;

      toast(t("spendzen.contact.success_msg"), {
        theme: "auto",
        type: "success",
        position: "bottom-right",
        transition: "flip",
      });

      trackEvent("contact_form_success", {
        subject: subjectInput.value,
      });
    })
    .on("error", (err: any) => {
      loading.value = false;
      console.error("[StableForm] error", err);
    })
    .on("sending", () => {
      loading.value = true;
      trackEvent("contact_form_submit", {
        subject: subjectInput.value,
      });
    });

  stableForm.subscribeTo(form.value);
});
</script>

<template>
  <div
    id="contactus"
    class="p-8 lg:p-16 flex flex-col justify-center space-y-6 lg:w-1/2 text-center lg:text-left scroll-mt-[130px]"
  >
    <h2 class="text-3xl md:text-5xl font-extrabold leading-tight">
      {{ title || t("spendzen.contact.title") }}<br />
      <span v-if="titleHighlight" :class="highlightClass">
        {{ titleHighlight }}
      </span>
      <span
        v-else-if="!title && t('spendzen.contact.title_highlight')"
        :class="highlightClass"
      >
        {{ t("spendzen.contact.title_highlight") }}
      </span>
    </h2>
    <p class="text-sm md:text-lg">
      {{ description || t("spendzen.contact.description") }}
    </p>
    <div class="flex items-center space-x-2 my-4">
      <hr class="flex-1" />
      <p>{{ t("spendzen.contact.or") }}</p>
      <hr class="flex-1" />
    </div>
    <button
      @click="openModal('spendzen')"
      class="w-full py-2 text-sm bg-white rounded-lg text-primary border border-primary font-semibold transition"
    >
      <TransitionGroup name="fade">
        <span v-show="!loading"> {{ t("spendzen.contact.demo_btn") }} </span>
      </TransitionGroup>
    </button>
  </div>

  <div
    class="bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 p-6 lg:w-1/2 rounded-b-2xl lg:rounded-r-2xl lg:rounded-bl-none"
  >
    <form ref="form" class="space-y-4" id="contact-form">
      <div class="space-y-1">
        <label
          for="name"
          class="text-sm font-medium text-gray-700 dark:text-gray-200"
          >{{ t("spendzen.contact.labels.name") }}</label
        >
        <input
          id="name"
          name="name"
          type="text"
          v-model="nameInput"
          :placeholder="t('spendzen.contact.placeholders.name')"
          required
          class="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 bg-white dark:bg-gray-700 focus:ring-0 outline-none focus:border-primary"
        />
      </div>

      <div class="space-y-1">
        <label
          for="email"
          class="text-sm font-medium text-gray-700 dark:text-gray-200"
          >{{ t("spendzen.contact.labels.email") }}</label
        >
        <input
          id="email"
          name="email"
          type="email"
          v-model="mailInput"
          :placeholder="t('spendzen.contact.placeholders.email')"
          required
          class="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 bg-white dark:bg-gray-700 focus:ring-0 outline-none focus:border-primary"
        />
      </div>

      <div class="space-y-1">
        <label
          for="subject"
          class="text-sm font-medium text-gray-700 dark:text-gray-200"
          >{{ t("spendzen.contact.labels.subject") }}</label
        >
        <select
          id="subject"
          name="subject"
          v-model="subjectInput"
          required
          class="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 bg-white dark:bg-gray-700 focus:ring-0 outline-none focus:border-primary appearance-none cursor-pointer"
        >
          <option value="" disabled selected>
            {{ t("spendzen.contact.placeholders.subject") }}
          </option>
          <option
            v-for="subject in subjects"
            :key="subject.key"
            :value="subject.key"
          >
            {{ subject.label }}
          </option>
        </select>
      </div>

      <div class="space-y-1">
        <label
          for="message"
          class="text-sm font-medium text-gray-700 dark:text-gray-200"
          >{{ t("spendzen.contact.labels.message") }}</label
        >
        <textarea
          id="message"
          name="message"
          v-model="messageInput"
          :placeholder="t('spendzen.contact.placeholders.message')"
          rows="3"
          required
          class="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 bg-white dark:bg-gray-700 focus:ring-0 outline-none focus:border-primary"
        ></textarea>
      </div>

      <button
        type="submit"
        class="w-full py-2 text-sm bg-primary rounded-lg text-white font-semibold transition"
      >
        <TransitionGroup name="fade">
          <span v-show="!loading"> {{ t("spendzen.contact.submit_btn") }} </span>
          <div class="flex items-center justify-center w-full" v-show="loading">
            <Spin />
          </div>
        </TransitionGroup>
      </button>
    </form>
  </div>
</template>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
